import { memo } from 'react';
import clsx from 'clsx';
import { DataTestConfig } from '@headless-workspace/config';
import { ComponentMemo } from '@headless-workspace/typings';
import { cva, VariantProps } from 'class-variance-authority';
import { PropsWithAriaLabel, PropsWithClassName } from '../../props';
import { GlowTypography } from '../typography';

export const chipStyle = cva('', {
    variants: {
        status: {
            success: 'bg-background-success-secondary',
            warning: 'bg-background-warning-emphazis',
            error: 'bg-background-error-red-secondary',
            promotion: 'bg-surface-promotion',
        },
        size: {
            small: 'min-w-iconChipSmall min-h-iconChipSmall max-w-iconChipSmall max-h-iconChipSmall',
            medium: 'min-w-iconChipMedium min-h-iconChipMedium max-w-iconChipMedium max-h-iconChipMedium',
        },
    },
    defaultVariants: {
        status: 'success',
        size: 'small',
    },
});

export type GlowChipProps = PropsWithAriaLabel &
    PropsWithClassName &
    VariantProps<typeof chipStyle> &
    (
        | {
              hasDigit: true;
              count: number;
          }
        | {
              hasDigit: false;
          }
    );

export const GlowChip: ComponentMemo<GlowChipProps> = memo(
    ({ className, ariaLabel, status = 'promotion', size = 'medium', ...props }) => {
        if (!props.hasDigit) {
            return (
                <div
                    aria-label={ariaLabel}
                    className={clsx(
                        'flex justify-center items-center',
                        'px-0.25',
                        'rounded-infinity',
                        chipStyle({ status, size }),
                        className,
                    )}
                    data-testid={'chip'}
                />
            );
        }

        return props.count > 0 ? (
            <div
                className={clsx(
                    'flex justify-center items-center',
                    'w-iconChipWithDigit h-iconChipWithDigit',
                    'px-0.25',
                    'rounded-infinity bg-surface-promotion',
                    className,
                )}
                data-testid={'chip'}
            >
                <GlowTypography
                    TagName={'span'}
                    text={props.count}
                    color={'primary-inverse-fixed'}
                    className={'tracking-normal'}
                    fontSize={'chip'}
                    aria-label={ariaLabel}
                    fontWeight={'bold'}
                    dataTestId={DataTestConfig.testIds.wishlistProductNumberIcon}
                />
            </div>
        ) : null;
    },
);
